import { useSyncExternalStore } from 'react';
import { ErrorDeApi, ErrorDeRed } from '../lib/api.js';
import { CLAVE_DE_MI, clienteDeConsultas } from '../lib/cliente-de-consultas.js';
import { leerSesion } from '../lib/sesion.js';
import {
  type EnvioPendiente,
  enviosPorSalir,
  instantaneaDeLaCola,
  type QueHacerConElError,
  type ResultadoDelVaciado,
  suscribirseALaCola,
  vaciarCola,
} from './almacen.js';
import * as api from './api.js';

/**
 * El vaciado de la cola · **uno solo, para toda la app** (H-1).
 *
 * Hasta acá `vaciarCola` se llamaba desde un único lugar: adentro de
 * `Formulario.tsx`, y sólo mientras esa pantalla estaba abierta. El recorrido
 * que eso dejaba afuera es el más probable de todos: el paciente responde sin
 * señal, cierra la app, al otro día la abre con conexión y cae en el Inicio. La
 * cola no salía, el Inicio le volvía a ofrecer el mismo check-in, la ventana
 * podía cerrarse, y el protocolo de silencio podía terminar escribiéndole a su
 * persona de contacto por alguien que **sí** había respondido.
 *
 * Ahora el vaciado se intenta cada vez que la app tiene sesión y hay algo
 * guardado (`intentarElEnvio`, P-132): al abrir, al recuperar la conexión y
 * cada vez que aparece una sesión (un enlace o el teléfono recordado), esté el
 * paciente en la pantalla que esté.
 * Con **una sola implementación**, y con **una sola consecuencia**: lo que pasa
 * después de que algo sale de la cola vive acá, no en el que llamó.
 */

/**
 * Manda la aplicación completa: abrir, guardar y cerrar, en ese orden.
 *
 * `abrir` es idempotente (mismo formulario e hito devuelven la misma
 * aplicación), así que reintentar un envío que falló a mitad no duplica nada.
 */
export async function enviarCompleto(envio: EnvioPendiente): Promise<void> {
  const aplicacion = await api.abrirFormulario(envio.formularioId, envio.hito);

  if (Object.keys(envio.respuestas).length > 0) {
    await api.guardarRespuestas(aplicacion.id, envio.respuestas);
  }

  // `tardio` no viaja: lo decide el servidor contra el ítem del plan. La hora
  // del dispositivo sí, como dato (H-6): el servidor la mira nada más que para
  // no marcar como tardía una respuesta hecha en término sin señal.
  await api.cerrarFormulario(aplicacion.id, {
    cierre: envio.cierre,
    duracionSegundos: envio.duracionSegundos,
    ...(envio.respondidoAt === undefined
      ? {}
      : { respondidoEnDispositivoAt: new Date(envio.respondidoAt).toISOString() }),
  });
}

/**
 * Qué hacer con un envío que falló · **el que decide es éste, no el almacén**.
 *
 * Se queda en la cola lo que puede salir más tarde, y sólo eso:
 *
 *   - **Sin respuesta del servidor** (`ErrorDeRed`): no hay señal. Es el caso
 *     para el que la cola existe.
 *   - **5xx** y **respuesta ilegible**: falló el servidor. Mañana anda.
 *   - **401**: la sesión se cortó. Se arregla reingresando, y el envío tiene
 *     que seguir ahí cuando eso pase.
 *   - **429**: pedimos demasiado. Hoy ninguno de los tres pasos lo devuelve
 *     —sólo el ingreso por enlace tiene ese límite—, y está contemplado igual
 *     porque el día que exista, esperar es exactamente lo correcto.
 *
 * **`409 aplicacion_cerrada` es entrega, no fallo.** Esa aplicación ya está
 * cerrada del otro lado, y el id salió de `abrir` en este mismo intento: quiere
 * decir que la respuesta llegó por otro camino (otra pestaña, el intento
 * anterior cuya respuesta se perdió). Reintentarlo para siempre sería prometerle
 * al paciente que algo está por salir cuando ya salió.
 *
 * **Cualquier otro 4xx es un rechazo definitivo.** Un `422 respuesta_invalida` o
 * `formulario_incompleto` después de un cambio de versión del formulario, un
 * `403`, un `404 sin_programa` porque el programa se cerró: el servidor no lo va
 * a aceptar nunca. Dejarlo en la cola con el Inicio diciendo "no tenés que
 * responder de nuevo" es el daño de H-1 otra vez.
 *
 * Un error que no sea ninguno de los dos tipos conocidos —un bug nuestro— se
 * queda: no se descarta el trabajo del paciente por una falla de este código.
 */
export function queHacerCon(error: unknown): QueHacerConElError {
  if (error instanceof ErrorDeRed) return 'se-queda';
  if (!(error instanceof ErrorDeApi)) return 'se-queda';

  if (error.estado >= 500 || error.codigo === 'respuesta_ilegible') return 'se-queda';
  if (error.estado === 401 || error.estado === 429) return 'se-queda';
  if (error.estado === 409 && error.codigo === 'aplicacion_cerrada') return 'entregado';

  return error.estado >= 400 ? 'rechazado' : 'se-queda';
}

// ── Lo que acaba de salir ───────────────────────────────────────────────────

/**
 * Los envíos que ya llegaron al servidor y cuyo `mi` todavía no se recargó.
 *
 * Entre que un envío sale de la cola y que el servidor vuelve a contestar qué
 * hay pendiente hay una ventana —de la invalidación al refetch, y con
 * `staleTime` de 30 segundos podía ser bastante más— en la que el Inicio ya no
 * lo ve en la cola y todavía lo ve pendiente: le ofrecería al paciente responder
 * de nuevo el check-in que acaba de mandar. Se recuerdan en memoria y se
 * olvidan cuando el servidor confirma.
 */
const recienEnviados = new Map<string, EnvioPendiente>();

function clave(envio: EnvioPendiente): string {
  return `${envio.formularioId}|${envio.hito ?? ''}`;
}

function anotarEnviado(envio: EnvioPendiente): void {
  recienEnviados.set(clave(envio), envio);
  cambio();
}

function olvidarLosEnviados(): void {
  if (recienEnviados.size === 0) return;
  recienEnviados.clear();
  cambio();
}

// ── El almacén que mira la pantalla ─────────────────────────────────────────

const mirando = new Set<() => void>();
let instantanea: readonly EnvioPendiente[] | null = null;

function cambio(): void {
  instantanea = null;
  for (const escucha of mirando) escucha();
}

// Lo que cambie en el almacenamiento también cambia esta vista.
suscribirseALaCola(cambio);

function suscribirse(escucha: () => void): () => void {
  mirando.add(escucha);
  return () => {
    mirando.delete(escucha);
  };
}

/**
 * Lo mismo que `useEnviosDelDispositivo`, sin React: lo que el dispositivo
 * tiene de este paciente y el servidor todavía no confirmó.
 */
export function enviosDelDispositivo(): readonly EnvioPendiente[] {
  if (instantanea) return instantanea;

  // Sin repetir: durante un instante del vaciado el mismo envío está en el
  // almacenamiento y en lo recién enviado, y contarlo dos veces haría que una
  // evaluación pareciera tener más escalas resueltas de las que tiene.
  const guardados = instantaneaDeLaCola();
  const claves = new Set(guardados.map(clave));
  instantanea = [
    ...guardados,
    ...[...recienEnviados.values()].filter((envio) => !claves.has(clave(envio))),
  ];
  return instantanea;
}

/**
 * Lo que el dispositivo tiene de este paciente y el servidor todavía no
 * confirmó: lo que espera conexión y lo que acaba de salir.
 *
 * Es lo que el Inicio cruza con lo pendiente del servidor. Lo **rechazado** no
 * está: ese contacto se vuelve a ofrecer.
 */
export function useEnviosDelDispositivo(): readonly EnvioPendiente[] {
  return useSyncExternalStore(suscribirse, enviosDelDispositivo, enviosDelDispositivo);
}

// ── El vaciado ──────────────────────────────────────────────────────────────

/**
 * Un vaciado a la vez.
 *
 * Los tres disparadores pueden coincidir —la app abre justo cuando vuelve la
 * conexión— y dos vaciados en paralelo mandarían cada envío dos veces. El
 * segundo no encola otro intento: se cuelga del que ya está corriendo.
 */
let enMarcha: Promise<ResultadoDelVaciado> | null = null;

async function corridaDelVaciado(
  enviar: (envio: EnvioPendiente) => Promise<void>,
): Promise<ResultadoDelVaciado> {
  const resultado = await vaciarCola(
    async (envio) => {
      await enviar(envio);
      anotarEnviado(envio);
    },
    (error, envio) => {
      const que = queHacerCon(error);
      // Un 409 también quiere decir que el servidor ya lo tiene: cuenta igual
      // para no volver a ofrecer el contacto.
      if (que === 'entregado') anotarEnviado(envio);
      return que;
    },
  );

  // **La consecuencia, en un solo lugar.** Si algo llegó al servidor, lo que el
  // servidor dice de este paciente cambió. Se espera al refetch antes de
  // olvidar lo recién enviado: soltarlo antes reabriría la ventana en la que el
  // Inicio ofrece un contacto que ya respondió.
  if (resultado.enviados > 0) {
    try {
      await clienteDeConsultas.invalidateQueries({ queryKey: CLAVE_DE_MI });
    } finally {
      olvidarLosEnviados();
    }
  }

  return resultado;
}

export function vaciarLaCola(
  enviar: (envio: EnvioPendiente) => Promise<void> = enviarCompleto,
): Promise<ResultadoDelVaciado> {
  if (enMarcha) return enMarcha;

  const corrida = corridaDelVaciado(enviar).finally(() => {
    enMarcha = null;
  });
  enMarcha = corrida;
  return corrida;
}

/**
 * **La regla, en una línea (P-132): cada vez que la app tiene sesión y hay algo
 * guardado, intenta enviarlo.**
 *
 * No son disparadores sueltos con su propia condición (eso fue lo que dejó
 * afuera la renovación con el teléfono recordado): quien sea que se entere de
 * que hay sesión o señal llama a esto, y esto decide. Sin sesión no se intenta,
 * porque el envío iría contra un 401 y volvería a la cola; sin nada guardado no
 * hay nada que hacer. Devuelve `null` cuando no intentó.
 */
export function intentarElEnvio(
  enviar: (envio: EnvioPendiente) => Promise<void> = enviarCompleto,
): Promise<ResultadoDelVaciado> | null {
  if (leerSesion() === null) return null;
  if (!enMarcha && enviosPorSalir().length === 0) return null;
  return vaciarLaCola(enviar);
}

/** Sólo para los tests: el vaciado en curso y lo recién enviado vuelven a cero. */
export function olvidarElVaciado(): void {
  enMarcha = null;
  recienEnviados.clear();
  instantanea = null;
}

export type { ResultadoDelVaciado };
